// Available events that can trigger playbooks
export const EVENTS = [
  {
    id: 'transaction.completed',
    name: 'Transaction Completed',
    description: 'Triggered when a payment transaction is successfully completed',
    category: 'payments',
    payload: {
      transaction_id: 'string',
      booking_reference: 'string',
      payment_link_id: 'string',
      payment_link_amount: 'number',
      transaction_amount: 'number',
      currency: 'string',
      payment_method: 'card | open_banking | direct_debit',
      payment_time: 'datetime',
      customer: {
        email: 'string',
        name: 'string'
      }
    }
  },
  {
    id: 'transaction.failed',
    name: 'Transaction Failed',
    description: 'Triggered when a payment transaction fails',
    category: 'payments',
    payload: {
      transaction_id: 'string',
      booking_reference: 'string',
      error_code: 'string',
      error_message: 'string'
    }
  },
  {
    id: 'refund.requested',
    name: 'Refund Requested',
    description: 'Triggered when a refund is requested',
    category: 'refunds',
    payload: {
      refund_id: 'string',
      transaction_id: 'string',
      amount: 'number',
      reason: 'string'
    }
  },
  {
    id: 'refund.completed',
    name: 'Refund Completed',
    description: 'Triggered when a refund is successfully processed',
    category: 'refunds',
    payload: {
      refund_id: 'string',
      transaction_id: 'string',
      amount: 'number'
    }
  },
  {
    id: 'chargeback.created',
    name: 'Chargeback Created',
    description: 'Triggered when a chargeback is initiated',
    category: 'disputes',
    payload: {
      chargeback_id: 'string',
      transaction_id: 'string',
      amount: 'number',
      reason_code: 'string'
    }
  },
  {
    id: 'payment_link.created',
    name: 'Payment Link Created',
    description: 'Triggered when a new payment link is created',
    category: 'payment_links',
    payload: {
      payment_link_id: 'string',
      amount: 'number',
      currency: 'string',
      reference: 'string'
    }
  },
  {
    id: 'booking.created',
    name: 'Booking Created',
    description: 'Triggered when a new booking is created',
    category: 'bookings',
    payload: {
      booking_id: 'string',
      reference: 'string',
      customer: {
        email: 'string',
        name: 'string'
      }
    }
  }
];

// Available integrations for playbook actions
export const INTEGRATIONS = [
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    icon: 'pipedrive',
    actions: ['search_deal', 'update_deal', 'create_deal', 'get_deal_fields'],
    description: 'CRM integration for deal management'
  },
  {
    id: 'xero',
    name: 'Xero',
    icon: 'xero',
    actions: ['search_contact', 'create_contact', 'search_invoice', 'create_invoice', 'create_payment'],
    description: 'Accounting integration for invoicing and payments'
  },
  {
    id: 'email',
    name: 'Email',
    icon: 'email',
    actions: ['send_email', 'send_template'],
    description: 'Send email notifications'
  },
  {
    id: 'slack',
    name: 'Slack',
    icon: 'slack',
    actions: ['send_message', 'send_alert'],
    description: 'Send Slack notifications'
  },
  {
    id: 'felloh',
    name: 'Felloh',
    icon: 'felloh',
    actions: ['get_transaction', 'get_booking', 'update_booking'],
    description: 'Internal Felloh operations'
  }
];

// Dummy data for playbooks in n8n workflow format
export const PLAYBOOKS = [
  {
      "id": 'f6a7b8c9-d0e1-4f2a-3b4c-5d6e7f8a9b2a',
    "name": "Felloh Transactions → Sage Accounting",
    "nodes": [
      {
        "parameters": {
          "httpMethod": "POST",
          "path": "felloh-transaction-webhook",
          "options": {
            "responseMode": "onReceived",
            "responseData": "allEntries"
          }
        },
        "id": "webhook-felloh",
        "name": "Felloh Webhook",
        "type": "n8n-nodes-base.webhook",
        "typeVersion": 2,
        "position": [240, 300],
        "webhookId": "felloh-transaction-webhook",
        "notes": "Receives transaction webhooks from Felloh. Configure this webhook URL in your Felloh dashboard under Settings → Webhooks."
      },
      {
        "parameters": {
          "conditions": {
            "options": {
              "caseSensitive": true,
              "leftValue": "",
              "typeValidation": "strict"
            },
            "conditions": [
              {
                "id": "condition-event-type",
                "leftValue": "={{ $json.body.event_type }}",
                "rightValue": "transaction.completed",
                "operator": {
                  "type": "string",
                  "operation": "equals"
                }
              }
            ],
            "combinator": "and"
          },
          "options": {}
        },
        "id": "filter-completed",
        "name": "Filter Completed Transactions",
        "type": "n8n-nodes-base.filter",
        "typeVersion": 2,
        "position": [460, 300],
        "notes": "Only process completed transactions. Adjust the event_type filter based on which Felloh webhook events you want to sync."
      },
      {
        "parameters": {
          "jsCode": "// Map Felloh transaction data to Sage-compatible format\n// Adjust field mappings based on your Felloh payload structure and Sage chart of accounts\n\nconst fellohData = $input.first().json.body;\nconst transaction = fellohData.data || fellohData;\n\n// Determine transaction type for Sage\nconst isPayment = transaction.type === 'payment' || transaction.type === 'collection';\nconst isRefund = transaction.type === 'refund';\n\n// Format date for Sage (ISO 8601)\nconst transactionDate = transaction.completed_at || transaction.created_at || new Date().toISOString();\nconst formattedDate = transactionDate.split('T')[0]; // YYYY-MM-DD\n\n// Calculate amounts\n// Felloh typically provides amounts in pence/cents — convert to pounds/dollars\nconst totalAmount = (transaction.amount || 0) / 100;\nconst feeAmount = (transaction.fee || transaction.felloh_fee || 0) / 100;\nconst netAmount = totalAmount - feeAmount;\n\n// Build Sage sales invoice / payment object\nconst sagePayload = {\n  // -- Contact / Customer --\n  contact_name: transaction.customer?.name || transaction.customer_name || 'Unknown Customer',\n  contact_email: transaction.customer?.email || transaction.customer_email || '',\n  \n  // -- Transaction Details --\n  felloh_transaction_id: transaction.id || transaction.transaction_id,\n  felloh_booking_reference: transaction.booking_reference || transaction.reference || '',\n  transaction_type: transaction.type || 'payment',\n  payment_method: transaction.payment_method || transaction.method || 'card',\n  currency: transaction.currency || 'GBP',\n  \n  // -- Amounts --\n  total_amount: totalAmount,\n  fee_amount: feeAmount,\n  net_amount: netAmount,\n  \n  // -- Dates --\n  transaction_date: formattedDate,\n  \n  // -- Sage Mapping --\n  // Adjust these ledger account IDs to match YOUR Sage chart of accounts\n  sage_ledger_account_id: isRefund ? 'REFUNDS_LEDGER_ID' : 'SALES_LEDGER_ID',\n  sage_bank_account_id: 'FELLOH_BANK_ACCOUNT_ID',\n  sage_fee_ledger_account_id: 'MERCHANT_FEES_LEDGER_ID',\n  \n  // -- Description --\n  description: `Felloh ${transaction.type || 'payment'} - Ref: ${transaction.booking_reference || transaction.reference || transaction.id}`,\n  \n  // -- Flags --\n  is_refund: isRefund,\n  is_payment: isPayment\n};\n\nreturn [{ json: sagePayload }];"
        },
        "id": "transform-data",
        "name": "Transform Felloh → Sage Format",
        "type": "n8n-nodes-base.code",
        "typeVersion": 2,
        "position": [680, 300],
        "notes": "Maps Felloh transaction fields to Sage-compatible format. IMPORTANT: Update the ledger account IDs (SALES_LEDGER_ID, FELLOH_BANK_ACCOUNT_ID, MERCHANT_FEES_LEDGER_ID) with your actual Sage account IDs."
      },
      {
        "parameters": {
          "conditions": {
            "options": {
              "caseSensitive": true,
              "leftValue": "",
              "typeValidation": "strict"
            },
            "conditions": [
              {
                "id": "condition-is-refund",
                "leftValue": "={{ $json.is_refund }}",
                "rightValue": true,
                "operator": {
                  "type": "boolean",
                  "operation": "equals"
                }
              }
            ],
            "combinator": "and"
          },
          "options": {}
        },
        "id": "route-transaction-type",
        "name": "Payment or Refund?",
        "type": "n8n-nodes-base.if",
        "typeVersion": 2,
        "position": [900, 300],
        "notes": "Routes payments and refunds to different Sage endpoints."
      },
      {
        "parameters": {
          "method": "POST",
          "url": "https://api.accounting.sage.com/v3.1/contact_payments",
          "authentication": "predefinedCredentialType",
          "nodeCredentialType": "sageAccountingOAuth2Api",
          "sendHeaders": true,
          "headerParameters": {
            "parameters": [
              {
                "name": "Content-Type",
                "value": "application/json"
              }
            ]
          },
          "sendBody": true,
          "specifyBody": "json",
          "jsonBody": "={\n  \"contact_payment\": {\n    \"transaction_type_id\": \"CUSTOMER_RECEIPT\",\n    \"contact_id\": \"{{ $json.sage_contact_id }}\",\n    \"bank_account_id\": \"{{ $json.sage_bank_account_id }}\",\n    \"date\": \"{{ $json.transaction_date }}\",\n    \"total_amount\": {{ $json.total_amount }},\n    \"reference\": \"{{ $json.felloh_booking_reference }}\",\n    \"payment_method_id\": \"CREDIT_DEBIT\"\n  }\n}",
          "options": {
            "response": {
              "response": {
                "fullResponse": true
              }
            }
          }
        },
        "id": "sage-create-payment",
        "name": "Sage: Create Payment",
        "type": "n8n-nodes-base.httpRequest",
        "typeVersion": 4.2,
        "position": [1140, 200],
        "notes": "Creates a contact payment in Sage Accounting. Uses OAuth2 credentials — configure your Sage API app credentials in n8n first. Adjust transaction_type_id and payment_method_id to match your Sage setup.",
        "credentials": {
          "sageAccountingOAuth2Api": {
            "id": "SAGE_CREDENTIAL_ID",
            "name": "Sage Accounting OAuth2"
          }
        }
      },
      {
        "parameters": {
          "method": "POST",
          "url": "https://api.accounting.sage.com/v3.1/contact_payments",
          "authentication": "predefinedCredentialType",
          "nodeCredentialType": "sageAccountingOAuth2Api",
          "sendHeaders": true,
          "headerParameters": {
            "parameters": [
              {
                "name": "Content-Type",
                "value": "application/json"
              }
            ]
          },
          "sendBody": true,
          "specifyBody": "json",
          "jsonBody": "={\n  \"contact_payment\": {\n    \"transaction_type_id\": \"CUSTOMER_REFUND\",\n    \"contact_id\": \"{{ $json.sage_contact_id }}\",\n    \"bank_account_id\": \"{{ $json.sage_bank_account_id }}\",\n    \"date\": \"{{ $json.transaction_date }}\",\n    \"total_amount\": {{ $json.total_amount }},\n    \"reference\": \"REFUND - {{ $json.felloh_booking_reference }}\",\n    \"payment_method_id\": \"CREDIT_DEBIT\"\n  }\n}",
          "options": {
            "response": {
              "response": {
                "fullResponse": true
              }
            }
          }
        },
        "id": "sage-create-refund",
        "name": "Sage: Create Refund",
        "type": "n8n-nodes-base.httpRequest",
        "typeVersion": 4.2,
        "position": [1140, 420],
        "notes": "Creates a refund/credit note in Sage Accounting for Felloh refund transactions.",
        "credentials": {
          "sageAccountingOAuth2Api": {
            "id": "SAGE_CREDENTIAL_ID",
            "name": "Sage Accounting OAuth2"
          }
        }
      },
      {
        "parameters": {
          "method": "POST",
          "url": "https://api.accounting.sage.com/v3.1/other_payments",
          "authentication": "predefinedCredentialType",
          "nodeCredentialType": "sageAccountingOAuth2Api",
          "sendHeaders": true,
          "headerParameters": {
            "parameters": [
              {
                "name": "Content-Type",
                "value": "application/json"
              }
            ]
          },
          "sendBody": true,
          "specifyBody": "json",
          "jsonBody": "={\n  \"other_payment\": {\n    \"transaction_type_id\": \"OTHER_PAYMENT\",\n    \"bank_account_id\": \"{{ $input.first().$node['Transform Felloh → Sage Format'].json.sage_bank_account_id }}\",\n    \"date\": \"{{ $input.first().$node['Transform Felloh → Sage Format'].json.transaction_date }}\",\n    \"total_amount\": {{ $input.first().$node['Transform Felloh → Sage Format'].json.fee_amount }},\n    \"reference\": \"Felloh fee - {{ $input.first().$node['Transform Felloh → Sage Format'].json.felloh_booking_reference }}\",\n    \"payment_lines\": [\n      {\n        \"ledger_account_id\": \"{{ $input.first().$node['Transform Felloh → Sage Format'].json.sage_fee_ledger_account_id }}\",\n        \"total_amount\": {{ $input.first().$node['Transform Felloh → Sage Format'].json.fee_amount }},\n        \"description\": \"Felloh merchant processing fee\"\n      }\n    ]\n  }\n}",
          "options": {
            "response": {
              "response": {
                "fullResponse": true
              }
            }
          }
        },
        "id": "sage-record-fee",
        "name": "Sage: Record Felloh Fee",
        "type": "n8n-nodes-base.httpRequest",
        "typeVersion": 4.2,
        "position": [1380, 300],
        "notes": "Records the Felloh processing fee as a separate expense in Sage. Only runs if fee_amount > 0.",
        "credentials": {
          "sageAccountingOAuth2Api": {
            "id": "SAGE_CREDENTIAL_ID",
            "name": "Sage Accounting OAuth2"
          }
        }
      },
      {
        "parameters": {
          "conditions": {
            "options": {
              "caseSensitive": true,
              "leftValue": "",
              "typeValidation": "strict"
            },
            "conditions": [
              {
                "id": "condition-has-fee",
                "leftValue": "={{ $json.fee_amount }}",
                "rightValue": 0,
                "operator": {
                  "type": "number",
                  "operation": "gt"
                }
              }
            ],
            "combinator": "and"
          },
          "options": {}
        },
        "id": "check-fee",
        "name": "Has Processing Fee?",
        "type": "n8n-nodes-base.if",
        "typeVersion": 2,
        "position": [1380, 120],
        "notes": "Only record a fee expense if the Felloh fee is greater than zero."
      },
      {
        "parameters": {
          "values": {
            "string": [
              {
                "name": "status",
                "value": "=Felloh txn {{ $json.felloh_transaction_id }} synced to Sage successfully"
              }
            ]
          },
          "options": {}
        },
        "id": "success-log",
        "name": "Log Success",
        "type": "n8n-nodes-base.set",
        "typeVersion": 3.4,
        "position": [1600, 300],
        "notes": "Logs successful sync. Replace with Slack/email notification node if desired."
      },
      {
        "parameters": {
          "method": "GET",
          "url": "=https://api.accounting.sage.com/v3.1/contacts?email={{ encodeURIComponent($json.contact_email) }}",
          "authentication": "predefinedCredentialType",
          "nodeCredentialType": "sageAccountingOAuth2Api",
          "options": {
            "response": {
              "response": {
                "fullResponse": true
              }
            }
          }
        },
        "id": "sage-lookup-contact",
        "name": "Sage: Lookup Contact",
        "type": "n8n-nodes-base.httpRequest",
        "typeVersion": 4.2,
        "position": [680, 520],
        "notes": "Looks up the customer in Sage by email. If not found, the next node creates a new contact.",
        "credentials": {
          "sageAccountingOAuth2Api": {
            "id": "SAGE_CREDENTIAL_ID",
            "name": "Sage Accounting OAuth2"
          }
        }
      },
      {
        "parameters": {
          "jsCode": "// Check if a Sage contact was found, or prepare to create one\nconst transformedData = $('Transform Felloh → Sage Format').first().json;\nconst lookupResponse = $input.first().json;\n\nlet sageContactId = null;\n\nif (lookupResponse.body && lookupResponse.body.$items && lookupResponse.body.$items.length > 0) {\n  // Contact exists in Sage\n  sageContactId = lookupResponse.body.$items[0].id;\n} \n\nreturn [{\n  json: {\n    ...transformedData,\n    sage_contact_id: sageContactId,\n    needs_contact_creation: sageContactId === null\n  }\n}];"
        },
        "id": "check-contact-exists",
        "name": "Check Contact Exists",
        "type": "n8n-nodes-base.code",
        "typeVersion": 2,
        "position": [900, 520],
        "notes": "Checks if the Sage contact lookup returned a match. If not, flags that a new contact needs to be created."
      },
      {
        "parameters": {
          "conditions": {
            "options": {
              "caseSensitive": true,
              "leftValue": "",
              "typeValidation": "strict"
            },
            "conditions": [
              {
                "id": "condition-needs-creation",
                "leftValue": "={{ $json.needs_contact_creation }}",
                "rightValue": true,
                "operator": {
                  "type": "boolean",
                  "operation": "equals"
                }
              }
            ],
            "combinator": "and"
          },
          "options": {}
        },
        "id": "needs-contact-creation",
        "name": "Need to Create Contact?",
        "type": "n8n-nodes-base.if",
        "typeVersion": 2,
        "position": [1120, 520]
      },
      {
        "parameters": {
          "method": "POST",
          "url": "https://api.accounting.sage.com/v3.1/contacts",
          "authentication": "predefinedCredentialType",
          "nodeCredentialType": "sageAccountingOAuth2Api",
          "sendHeaders": true,
          "headerParameters": {
            "parameters": [
              {
                "name": "Content-Type",
                "value": "application/json"
              }
            ]
          },
          "sendBody": true,
          "specifyBody": "json",
          "jsonBody": "={\n  \"contact\": {\n    \"name\": \"{{ $json.contact_name }}\",\n    \"contact_type_ids\": [\"CUSTOMER\"],\n    \"email\": \"{{ $json.contact_email }}\",\n    \"notes\": \"Auto-created from Felloh transaction {{ $json.felloh_transaction_id }}\"\n  }\n}",
          "options": {
            "response": {
              "response": {
                "fullResponse": true
              }
            }
          }
        },
        "id": "sage-create-contact",
        "name": "Sage: Create Contact",
        "type": "n8n-nodes-base.httpRequest",
        "typeVersion": 4.2,
        "position": [1340, 620],
        "notes": "Creates a new customer contact in Sage when one doesn't already exist.",
        "credentials": {
          "sageAccountingOAuth2Api": {
            "id": "SAGE_CREDENTIAL_ID",
            "name": "Sage Accounting OAuth2"
          }
        }
      },
      {
        "parameters": {
          "jsCode": "// Merge the new contact ID back into the transaction data\nconst prevData = $('Check Contact Exists').first().json;\nconst createResponse = $input.first().json;\n\nlet contactId = prevData.sage_contact_id;\n\nif (createResponse.body && createResponse.body.id) {\n  contactId = createResponse.body.id;\n}\n\nreturn [{\n  json: {\n    ...prevData,\n    sage_contact_id: contactId\n  }\n}];"
        },
        "id": "merge-contact-id",
        "name": "Merge Contact ID",
        "type": "n8n-nodes-base.code",
        "typeVersion": 2,
        "position": [1340, 420]
      }
    ],
    "connections": {
      "Felloh Webhook": {
        "main": [
          [
            {
              "node": "Filter Completed Transactions",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Filter Completed Transactions": {
        "main": [
          [
            {
              "node": "Transform Felloh → Sage Format",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Transform Felloh → Sage Format": {
        "main": [
          [
            {
              "node": "Sage: Lookup Contact",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Sage: Lookup Contact": {
        "main": [
          [
            {
              "node": "Check Contact Exists",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Check Contact Exists": {
        "main": [
          [
            {
              "node": "Need to Create Contact?",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Need to Create Contact?": {
        "main": [
          [
            {
              "node": "Sage: Create Contact",
              "type": "main",
              "index": 0
            }
          ],
          [
            {
              "node": "Payment or Refund?",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Sage: Create Contact": {
        "main": [
          [
            {
              "node": "Merge Contact ID",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Merge Contact ID": {
        "main": [
          [
            {
              "node": "Payment or Refund?",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Payment or Refund?": {
        "main": [
          [
            {
              "node": "Sage: Create Refund",
              "type": "main",
              "index": 0
            }
          ],
          [
            {
              "node": "Sage: Create Payment",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Sage: Create Payment": {
        "main": [
          [
            {
              "node": "Has Processing Fee?",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Sage: Create Refund": {
        "main": [
          [
            {
              "node": "Has Processing Fee?",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Has Processing Fee?": {
        "main": [
          [
            {
              "node": "Sage: Record Felloh Fee",
              "type": "main",
              "index": 0
            }
          ],
          [
            {
              "node": "Log Success",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Sage: Record Felloh Fee": {
        "main": [
          [
            {
              "node": "Log Success",
              "type": "main",
              "index": 0
            }
          ]
        ]
      }
    },
    "settings": {
      "executionOrder": "v1"
    },
    "staticData": null,
    "tags": [
      {
        "name": "Felloh",
        "id": "tag-felloh"
      },
      {
        "name": "Sage Accounting",
        "id": "tag-sage"
      },
      {
        "name": "Payments",
        "id": "tag-payments"
      }
    ],
    "meta": {
      "templateCredsSetupCompleted": false,
      "instanceId": ""
    },
    "pinData": {}
  },
  {
    "id": 'f6a7b8c9-d0e1-4f2a-3b4c-5d6e7f8a9b0a',
    "name": "Felloh Transactions → Xero",
    "nodes": [
      {
        "parameters": {
          "httpMethod": "POST",
          "path": "felloh-webhook",
          "responseMode": "onReceived",
          "responseData": "allEntries",
          "options": {}
        },
        "id": "webhook-trigger",
        "name": "Felloh Webhook Trigger",
        "type": "n8n-nodes-base.webhook",
        "typeVersion": 2,
        "position": [240, 300],
        "webhookId": "felloh-webhook"
      },
      {
        "parameters": {
          "rule": {
            "interval": [
              {
                "field": "hours",
                "hoursInterval": 1
              }
            ]
          }
        },
        "id": "schedule-trigger",
        "name": "Scheduled Poll (Alternative)",
        "type": "n8n-nodes-base.scheduleTrigger",
        "typeVersion": 1.2,
        "position": [240, 560],
        "disabled": true
      },
      {
        "parameters": {
          "method": "POST",
          "url": "https://api.felloh.com/token",
          "sendHeaders": true,
          "headerParameters": {
            "parameters": [
              {
                "name": "Content-Type",
                "value": "application/json"
              }
            ]
          },
          "sendBody": true,
          "specifyBody": "json",
          "jsonBody": "={{ JSON.stringify({ \"public_key\": $credentials.fellohPublicKey, \"private_key\": $credentials.fellohPrivateKey }) }}",
          "options": {}
        },
        "id": "felloh-auth",
        "name": "Felloh: Get Auth Token",
        "type": "n8n-nodes-base.httpRequest",
        "typeVersion": 4.2,
        "position": [480, 560],
        "credentials": {
          "httpCustomAuth": {
            "id": "FELLOH_CREDENTIAL_ID",
            "name": "Felloh API Keys"
          }
        },
        "notes": "⚠️ SETUP: Create a 'Header Auth' credential with your Felloh public_key and private_key. Alternatively, hardcode them in the JSON body for testing."
      },
      {
        "parameters": {
          "method": "POST",
          "url": "https://api.felloh.com/agent/transactions",
          "authentication": "genericCredentialType",
          "sendHeaders": true,
          "headerParameters": {
            "parameters": [
              {
                "name": "Content-Type",
                "value": "application/json"
              },
              {
                "name": "Authorization",
                "value": "=Bearer {{ $json.data.token }}"
              }
            ]
          },
          "sendBody": true,
          "specifyBody": "json",
          "jsonBody": "={{ JSON.stringify({ \"organisation\": \"YOUR_FELLOH_ORG_ID\", \"skip\": 0, \"take\": 50, \"status\": [\"COMPLETE\"] }) }}",
          "options": {}
        },
        "id": "felloh-fetch-transactions",
        "name": "Felloh: Fetch Transactions",
        "type": "n8n-nodes-base.httpRequest",
        "typeVersion": 4.2,
        "position": [700, 560],
        "notes": "⚠️ SETUP: Replace YOUR_FELLOH_ORG_ID with your actual Felloh organisation ID. Adjust 'status' filter and 'take' as needed."
      },
      {
        "parameters": {
          "fieldToSplitOut": "data",
          "options": {}
        },
        "id": "split-transactions",
        "name": "Split Transactions",
        "type": "n8n-nodes-base.splitOut",
        "typeVersion": 1,
        "position": [920, 560]
      },
      {
        "parameters": {
          "conditions": {
            "options": {
              "caseSensitive": true,
              "leftValue": "",
              "typeValidation": "strict"
            },
            "conditions": [
              {
                "id": "condition-status",
                "leftValue": "={{ $json.status }}",
                "rightValue": "COMPLETE",
                "operator": {
                  "type": "string",
                  "operation": "equals"
                }
              }
            ],
            "combinator": "and"
          },
          "options": {}
        },
        "id": "filter-webhook",
        "name": "Filter: Complete Only",
        "type": "n8n-nodes-base.filter",
        "typeVersion": 2,
        "position": [480, 300]
      },
      {
        "parameters": {
          "jsCode": "// ============================================================\n// MAP FELLOH TRANSACTION → XERO INVOICE DATA\n// ============================================================\n// This node transforms the Felloh transaction payload into\n// the fields needed to create a Xero contact + invoice.\n//\n// Felloh amounts are in the LOWEST denomination (pence/cents),\n// so we divide by 100 to get the actual currency amount.\n// ============================================================\n\nconst items = $input.all();\nconst results = [];\n\nfor (const item of items) {\n  const tx = item.json;\n\n  // --- Extract transaction fields ---\n  // Webhook payload uses 'amount', 'booking.id', 'booking.booking_reference', etc.\n  // Polling payload may nest differently under 'data' array items.\n  const amount = (tx.amount || tx.value || 0) / 100;\n  const currency = (tx.currency || 'GBP').replace('GBX', 'GBP').replace('USX', 'USD').replace('EUX', 'EUR');\n  const bookingRef = tx.booking?.booking_reference || tx.booking_reference || 'N/A';\n  const bookingId = tx.booking?.id || tx.booking_id || '';\n  const customerName = tx.booking?.customer_name || tx.customer_name || tx.customer?.name || 'Felloh Customer';\n  const customerEmail = tx.booking?.customer_email || tx.customer_email || tx.customer?.email || '';\n  const transactionId = tx.id || tx.transaction_id || '';\n  const completedAt = tx.completed_at || tx.created_at || new Date().toISOString();\n  const paymentMethod = tx.payment_method || tx.type || 'Card';\n  const status = tx.status || 'COMPLETE';\n  const paymentLinkId = tx.payment_link?.id || tx.payment_link_id || '';\n\n  // --- Build due date (30 days from completion or today) ---\n  const completedDate = new Date(completedAt);\n  const dueDate = new Date(completedDate);\n  dueDate.setDate(dueDate.getDate() + 30);\n\n  // --- Format dates as YYYY-MM-DD for Xero ---\n  const formatDate = (d) => d.toISOString().split('T')[0];\n\n  results.push({\n    json: {\n      // Contact fields for Xero\n      contactName: customerName,\n      contactEmail: customerEmail,\n\n      // Invoice fields for Xero\n      invoiceType: 'ACCREC',  // Accounts Receivable (sales invoice)\n      invoiceReference: `FELLOH-${bookingRef}`,\n      invoiceDate: formatDate(completedDate),\n      dueDate: formatDate(dueDate),\n      currency: currency,\n      lineDescription: `Felloh Payment - Booking ${bookingRef} (Txn: ${transactionId})`,\n      lineAmount: amount,\n      lineAccountCode: '200',  // ⚠️ CHANGE THIS to your Xero Sales account code\n      invoiceStatus: 'AUTHORISED',\n\n      // Metadata for reference\n      fellohTransactionId: transactionId,\n      fellohBookingId: bookingId,\n      fellohBookingRef: bookingRef,\n      fellohPaymentMethod: paymentMethod,\n      fellohCompletedAt: completedAt,\n      fellohPaymentLinkId: paymentLinkId,\n      fellohStatus: status,\n      fellohAmountRaw: tx.amount || tx.value || 0\n    }\n  });\n}\n\nreturn results;"
        },
        "id": "map-to-xero",
        "name": "Map Felloh → Xero Format",
        "type": "n8n-nodes-base.code",
        "typeVersion": 2,
        "position": [780, 300]
      },
      {
        "parameters": {
          "method": "POST",
          "url": "https://api.xero.com/api.xro/2.0/Contacts",
          "authentication": "predefinedCredentialType",
          "nodeCredentialType": "xeroOAuth2Api",
          "sendHeaders": true,
          "headerParameters": {
            "parameters": [
              {
                "name": "Content-Type",
                "value": "application/json"
              },
              {
                "name": "xero-tenant-id",
                "value": "YOUR_XERO_TENANT_ID"
              }
            ]
          },
          "sendBody": true,
          "specifyBody": "json",
          "jsonBody": "={{ JSON.stringify({ \"Contacts\": [{ \"Name\": $json.contactName, \"EmailAddress\": $json.contactEmail }] }) }}",
          "options": {
            "response": {
              "response": {
                "neverError": true
              }
            }
          }
        },
        "id": "xero-upsert-contact",
        "name": "Xero: Create/Find Contact",
        "type": "n8n-nodes-base.httpRequest",
        "typeVersion": 4.2,
        "position": [1020, 300],
        "credentials": {
          "xeroOAuth2Api": {
            "id": "XERO_CREDENTIAL_ID",
            "name": "Xero OAuth2"
          }
        },
        "notes": "⚠️ SETUP: Replace YOUR_XERO_TENANT_ID with your Xero tenant ID. Set up Xero OAuth2 credentials in n8n. This will create the contact or return an existing one if the name matches.",
        "onError": "continueRegularOutput"
      },
      {
        "parameters": {
          "jsCode": "// ============================================================\n// EXTRACT XERO CONTACT ID\n// ============================================================\n// After creating/finding the contact, extract the ContactID\n// to use when creating the invoice.\n// If contact creation failed (duplicate), search for it.\n// ============================================================\n\nconst item = $input.first();\nconst prevData = $('Map Felloh → Xero Format').first().json;\nlet contactId = '';\n\ntry {\n  // Try to get ContactID from the response\n  if (item.json.Contacts && item.json.Contacts.length > 0) {\n    contactId = item.json.Contacts[0].ContactID;\n  }\n} catch (e) {\n  // If failed, we'll pass empty and handle in the invoice node\n}\n\nreturn [{\n  json: {\n    ...prevData,\n    xeroContactId: contactId\n  }\n}];"
        },
        "id": "extract-contact-id",
        "name": "Extract Contact ID",
        "type": "n8n-nodes-base.code",
        "typeVersion": 2,
        "position": [1240, 300]
      },
      {
        "parameters": {
          "method": "POST",
          "url": "https://api.xero.com/api.xro/2.0/Invoices",
          "authentication": "predefinedCredentialType",
          "nodeCredentialType": "xeroOAuth2Api",
          "sendHeaders": true,
          "headerParameters": {
            "parameters": [
              {
                "name": "Content-Type",
                "value": "application/json"
              },
              {
                "name": "xero-tenant-id",
                "value": "YOUR_XERO_TENANT_ID"
              }
            ]
          },
          "sendBody": true,
          "specifyBody": "json",
          "jsonBody": "={{ JSON.stringify({ \"Invoices\": [{ \"Type\": $json.invoiceType, \"Contact\": { \"ContactID\": $json.xeroContactId || undefined, \"Name\": $json.contactName }, \"Date\": $json.invoiceDate, \"DueDate\": $json.dueDate, \"Reference\": $json.invoiceReference, \"Status\": $json.invoiceStatus, \"CurrencyCode\": $json.currency, \"LineItems\": [{ \"Description\": $json.lineDescription, \"Quantity\": 1, \"UnitAmount\": $json.lineAmount, \"AccountCode\": $json.lineAccountCode, \"TaxType\": \"OUTPUT2\" }] }] }) }}",
          "options": {}
        },
        "id": "xero-create-invoice",
        "name": "Xero: Create Invoice",
        "type": "n8n-nodes-base.httpRequest",
        "typeVersion": 4.2,
        "position": [1460, 300],
        "credentials": {
          "xeroOAuth2Api": {
            "id": "XERO_CREDENTIAL_ID",
            "name": "Xero OAuth2"
          }
        },
        "notes": "⚠️ SETUP: Replace YOUR_XERO_TENANT_ID. Adjust TaxType (OUTPUT2 = 20% VAT, NONE = no tax, etc.) and AccountCode to match your Xero chart of accounts."
      },
      {
        "parameters": {
          "jsCode": "// ============================================================\n// LOG RESULT SUMMARY\n// ============================================================\nconst invoice = $input.first().json;\nconst prevData = $('Map Felloh → Xero Format').first().json;\n\nlet invoiceId = '';\nlet invoiceNumber = '';\ntry {\n  invoiceId = invoice.Invoices?.[0]?.InvoiceID || 'unknown';\n  invoiceNumber = invoice.Invoices?.[0]?.InvoiceNumber || 'unknown';\n} catch (e) {}\n\nreturn [{\n  json: {\n    success: true,\n    xeroInvoiceId: invoiceId,\n    xeroInvoiceNumber: invoiceNumber,\n    fellohTransactionId: prevData.fellohTransactionId,\n    fellohBookingRef: prevData.fellohBookingRef,\n    amount: prevData.lineAmount,\n    currency: prevData.currency,\n    message: `Invoice ${invoiceNumber} created in Xero for Felloh booking ${prevData.fellohBookingRef} (£${prevData.lineAmount})`\n  }\n}];"
        },
        "id": "log-result",
        "name": "Log Result",
        "type": "n8n-nodes-base.code",
        "typeVersion": 2,
        "position": [1680, 300]
      }
    ],
    "connections": {
      "Felloh Webhook Trigger": {
        "main": [
          [
            {
              "node": "Filter: Complete Only",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Scheduled Poll (Alternative)": {
        "main": [
          [
            {
              "node": "Felloh: Get Auth Token",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Felloh: Get Auth Token": {
        "main": [
          [
            {
              "node": "Felloh: Fetch Transactions",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Felloh: Fetch Transactions": {
        "main": [
          [
            {
              "node": "Split Transactions",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Split Transactions": {
        "main": [
          [
            {
              "node": "Map Felloh → Xero Format",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Filter: Complete Only": {
        "main": [
          [
            {
              "node": "Map Felloh → Xero Format",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Map Felloh → Xero Format": {
        "main": [
          [
            {
              "node": "Xero: Create/Find Contact",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Xero: Create/Find Contact": {
        "main": [
          [
            {
              "node": "Extract Contact ID",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Extract Contact ID": {
        "main": [
          [
            {
              "node": "Xero: Create Invoice",
              "type": "main",
              "index": 0
            }
          ]
        ]
      },
      "Xero: Create Invoice": {
        "main": [
          [
            {
              "node": "Log Result",
              "type": "main",
              "index": 0
            }
          ]
        ]
      }
    },
    "settings": {
      "executionOrder": "v1"
    },
    "staticData": null,
    "tags": [
      {
        "name": "Felloh",
        "id": "1"
      },
      {
        "name": "Xero",
        "id": "2"
      },
      {
        "name": "Accounting",
        "id": "3"
      }
    ],
    "pinData": {},
    // Tree structure for visual branching (derived from n8n structure)
    workflowTree: {
      id: 'webhook-trigger',
      name: 'Felloh Webhook Trigger',
      type: 'n8n-nodes-base.webhook',
      integration: 'felloh',
      description: 'Receives transaction webhooks from Felloh',
      children: [
        {
          id: 'filter-webhook',
          name: 'Filter: Complete Only',
          type: 'n8n-nodes-base.filter',
          description: 'Only process completed transactions',
          children: [
            {
              id: 'map-to-xero',
              name: 'Map Felloh → Xero Format',
              type: 'n8n-nodes-base.code',
              description: 'Transform transaction data for Xero',
              children: [
                {
                  id: 'xero-upsert-contact',
                  name: 'Xero: Create/Find Contact',
                  type: 'n8n-nodes-base.httpRequest',
                  integration: 'xero',
                  description: 'Create or find existing contact in Xero',
                  children: [
                    {
                      id: 'extract-contact-id',
                      name: 'Extract Contact ID',
                      type: 'n8n-nodes-base.code',
                      description: 'Extract the Xero Contact ID for invoice creation',
                      children: [
                        {
                          id: 'xero-create-invoice',
                          name: 'Xero: Create Invoice',
                          type: 'n8n-nodes-base.httpRequest',
                          integration: 'xero',
                          description: 'Create sales invoice in Xero',
                          children: [
                            {
                              id: 'log-result',
                              name: 'Log Result',
                              type: 'n8n-nodes-base.code',
                              description: 'Log success summary'
                            }
                          ]
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    // Alternative scheduled polling branch (disabled by default)
    alternateTree: {
      id: 'schedule-trigger',
      name: 'Scheduled Poll (Alternative)',
      type: 'n8n-nodes-base.scheduleTrigger',
      integration: 'felloh',
      description: 'Poll Felloh API on schedule (disabled)',
      disabled: true,
      children: [
        {
          id: 'felloh-auth',
          name: 'Felloh: Get Auth Token',
          type: 'n8n-nodes-base.httpRequest',
          integration: 'felloh',
          description: 'Authenticate with Felloh API',
          children: [
            {
              id: 'felloh-fetch-transactions',
              name: 'Felloh: Fetch Transactions',
              type: 'n8n-nodes-base.httpRequest',
              integration: 'felloh',
              description: 'Fetch completed transactions from Felloh',
              children: [
                {
                  id: 'split-transactions',
                  name: 'Split Transactions',
                  type: 'n8n-nodes-base.splitOut',
                  description: 'Process each transaction individually'
                }
              ]
            }
          ]
        }
      ]
    }
  },
  {
    id: 'f6a7b8c9-d0e1-4f2a-3b4c-5d6e7f8a9b0c',
    meta: {
      enabled: true,
      runs: 234,
      lastRun: '2025-01-15T16:22:00Z',
      tags: ['pipedrive', 'xero', 'invoicing', 'payments', 'integration'],
      integrations: ['pipedrive', 'xero', 'email']
    },
    name: 'Pipedrive & Xero Payment Sync',
    active: true,
    versionId: 'v1.0',
    settings: { executionOrder: 'v1' },
    nodes: [
      {
        id: 'start',
        name: 'New Transaction',
        type: 'n8n-nodes-felloh.trigger',
        typeVersion: 1,
        position: [250, 300],
        parameters: { event: 'transaction.completed' },
        credentials: { fellohApi: { id: '1', name: 'Felloh API' } }
      },
      {
        id: 'find-deal',
        name: 'Find Deal',
        type: 'n8n-nodes-base.pipedrive',
        typeVersion: 1,
        position: [450, 300],
        parameters: {
          operation: 'search',
          resource: 'deal',
          term: '={{ $json.booking_reference }}',
          searchField: 'custom_fields'
        },
        credentials: { pipedriveApi: { id: '3', name: 'Pipedrive' } }
      },
      {
        id: 'check-deal',
        name: 'Deal Found?',
        type: 'n8n-nodes-base.if',
        typeVersion: 1,
        position: [650, 300],
        parameters: {
          conditions: { boolean: [{ value1: '={{ $json.data.length > 0 }}', value2: true }] }
        }
      },
      {
        id: 'find-invoice',
        name: 'Find Invoice',
        type: 'n8n-nodes-base.xero',
        typeVersion: 1,
        position: [850, 200],
        parameters: { operation: 'get', resource: 'invoice', invoiceId: '={{ $json.invoice_number }}' },
        credentials: { xeroOAuth2Api: { id: '4', name: 'Xero' } }
      },
      {
        id: 'check-invoice',
        name: 'Invoice Found?',
        type: 'n8n-nodes-base.if',
        typeVersion: 1,
        position: [1050, 200],
        parameters: { conditions: { boolean: [{ value1: '={{ $json.Invoices.length > 0 }}', value2: true }] } }
      },
      {
        id: 'split-payment',
        name: 'Split by Payment Method',
        type: 'n8n-nodes-base.switch',
        typeVersion: 2,
        position: [1250, 150],
        parameters: {
          dataType: 'string',
          value1: '={{ $json.payment_method }}',
          rules: {
            rules: [
              { value2: 'card', output: 0 },
              { value2: 'open_banking', output: 1 }
            ]
          }
        }
      },
      {
        id: 'card-payment',
        name: 'Create Payment (Card)',
        type: 'n8n-nodes-base.xero',
        typeVersion: 1,
        position: [1450, 100],
        parameters: {
          operation: 'create',
          resource: 'payment',
          invoiceId: '={{ $json.invoice_id }}',
          account: 'Trust Payments CC',
          amount: '={{ $json.payment_link_amount }}'
        }
      },
      {
        id: 'ob-payment',
        name: 'Create Payment (OB)',
        type: 'n8n-nodes-base.xero',
        typeVersion: 1,
        position: [1450, 200],
        parameters: {
          operation: 'create',
          resource: 'payment',
          invoiceId: '={{ $json.invoice_id }}',
          account: 'Natwest Current',
          amount: '={{ $json.payment_link_amount }}'
        }
      },
      {
        id: 'create-invoice',
        name: 'Create Sales Invoice',
        type: 'n8n-nodes-base.xero',
        typeVersion: 1,
        position: [1250, 300],
        parameters: {
          operation: 'create',
          resource: 'invoice',
          type: 'ACCREC',
          contact: '={{ $json.contact_id }}',
          status: 'DRAFT'
        }
      },
      {
        id: 'notify-error',
        name: 'Send Alert',
        type: 'n8n-nodes-base.emailSend',
        typeVersion: 2,
        position: [850, 450],
        parameters: {
          toEmail: 'accounts@company.com',
          subject: 'Felloh Payment - No Deal Found',
          text: 'Payment received for {{ $json.booking_reference }} but no matching deal found.'
        }
      }
    ],
    connections: {
      'start': { main: [[{ node: 'find-deal', type: 'main', index: 0 }]] },
      'find-deal': { main: [[{ node: 'check-deal', type: 'main', index: 0 }]] },
      'check-deal': { main: [[{ node: 'find-invoice', type: 'main', index: 0 }], [{ node: 'notify-error', type: 'main', index: 0 }]] },
      'find-invoice': { main: [[{ node: 'check-invoice', type: 'main', index: 0 }]] },
      'check-invoice': { main: [[{ node: 'split-payment', type: 'main', index: 0 }], [{ node: 'create-invoice', type: 'main', index: 0 }]] },
      'split-payment': { main: [[{ node: 'card-payment', type: 'main', index: 0 }], [{ node: 'ob-payment', type: 'main', index: 0 }]] }
    },
    // Tree structure for visual branching (derived from n8n structure)
    workflowTree: {
      id: 'start',
      name: 'New Transaction',
      type: 'n8n-nodes-felloh.trigger',
      integration: 'felloh',
      description: 'Triggered when a transaction is completed',
      children: [
        {
          id: 'find-deal',
          name: 'Find Deal',
          type: 'n8n-nodes-base.pipedrive',
          integration: 'pipedrive',
          description: 'Search for deal by payment reference',
          children: [
            {
              id: 'check-deal',
              name: 'Deal Found?',
              type: 'n8n-nodes-base.if',
              description: 'Check if matching deal exists',
              branches: [
                {
                  id: 'deal-success',
                  label: 'Success',
                  type: 'success',
                  children: [
                    {
                      id: 'find-invoice',
                      name: 'Find Invoice',
                      type: 'n8n-nodes-base.xero',
                      integration: 'xero',
                      description: 'Search for existing invoice',
                      children: [
                        {
                          id: 'check-invoice',
                          name: 'Invoice Found?',
                          type: 'n8n-nodes-base.if',
                          description: 'Check if invoice exists',
                          branches: [
                            {
                              id: 'invoice-success',
                              label: 'Success',
                              type: 'success',
                              children: [
                                {
                                  id: 'split-payment',
                                  name: 'Split by Payment Method',
                                  type: 'n8n-nodes-base.switch',
                                  description: 'Route based on payment type',
                                  branches: [
                                    {
                                      id: 'card-path',
                                      label: 'Card',
                                      type: 'path',
                                      children: [
                                        {
                                          id: 'card-payment',
                                          name: 'Create Payment',
                                          type: 'n8n-nodes-base.xero',
                                          integration: 'xero',
                                          description: 'Post to Trust Payments CC'
                                        }
                                      ]
                                    },
                                    {
                                      id: 'ob-path',
                                      label: 'Open Banking',
                                      type: 'path',
                                      children: [
                                        {
                                          id: 'ob-payment',
                                          name: 'Create Payment',
                                          type: 'n8n-nodes-base.xero',
                                          integration: 'xero',
                                          description: 'Post to Natwest Current'
                                        }
                                      ]
                                    }
                                  ]
                                }
                              ]
                            },
                            {
                              id: 'invoice-error',
                              label: 'Not Found',
                              type: 'error',
                              children: [
                                {
                                  id: 'create-invoice',
                                  name: 'Create Sales Invoice',
                                  type: 'n8n-nodes-base.xero',
                                  integration: 'xero',
                                  description: 'Create new draft invoice'
                                }
                              ]
                            }
                          ]
                        }
                      ]
                    }
                  ]
                },
                {
                  id: 'deal-error',
                  label: 'Error',
                  type: 'error',
                  children: [
                    {
                      id: 'notify-error',
                      name: 'Send Alert',
                      type: 'n8n-nodes-base.emailSend',
                      integration: 'email',
                      description: 'Notify team of missing deal'
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    // Version history
    versions: [
      {
        id: 'v1.0',
        version: '1.0',
        createdAt: '2024-12-01T10:00:00Z',
        createdBy: 'Adam Smith',
        changeDescription: 'Initial version - Basic Pipedrive to Xero payment sync',
        isCurrent: false
      },
      {
        id: 'v1.1',
        version: '1.1',
        createdAt: '2024-12-15T14:30:00Z',
        createdBy: 'Adam Smith',
        changeDescription: 'Added payment method splitting for Card vs Open Banking',
        isCurrent: false
      },
      {
        id: 'v1.2',
        version: '1.2',
        createdAt: '2025-01-05T09:15:00Z',
        createdBy: 'Sarah Johnson',
        changeDescription: 'Added error notification when deal not found in Pipedrive',
        isCurrent: false
      },
      {
        id: 'v1.3',
        version: '1.3',
        createdAt: '2025-01-10T16:45:00Z',
        createdBy: 'Adam Smith',
        changeDescription: 'Added invoice creation step when invoice not found in Xero',
        isCurrent: true
      }
    ],
    // Execution logs
    logs: [
      {
        id: 'log-001',
        executionId: 'exec-a1b2c3d4',
        startedAt: '2025-01-15T16:22:00Z',
        completedAt: '2025-01-15T16:22:03Z',
        duration: 3200,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0892', payment_method: 'card', amount: 1250.00 },
        stepsExecuted: 7,
        version: '1.3'
      },
      {
        id: 'log-002',
        executionId: 'exec-b2c3d4e5',
        startedAt: '2025-01-15T15:10:00Z',
        completedAt: '2025-01-15T15:10:02Z',
        duration: 2100,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0891', payment_method: 'open_banking', amount: 890.50 },
        stepsExecuted: 7,
        version: '1.3'
      },
      {
        id: 'log-003',
        executionId: 'exec-c3d4e5f6',
        startedAt: '2025-01-15T14:05:00Z',
        completedAt: '2025-01-15T14:05:04Z',
        duration: 4500,
        status: 'error',
        triggerData: { booking_reference: 'FEL-2025-0890', payment_method: 'card', amount: 2100.00 },
        stepsExecuted: 3,
        error: { step: 'Find Deal', message: 'Pipedrive API rate limit exceeded' },
        version: '1.3'
      },
      {
        id: 'log-004',
        executionId: 'exec-d4e5f6a7',
        startedAt: '2025-01-15T12:30:00Z',
        completedAt: '2025-01-15T12:30:05Z',
        duration: 5200,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0889', payment_method: 'card', amount: 450.00 },
        stepsExecuted: 8,
        version: '1.3'
      },
      {
        id: 'log-005',
        executionId: 'exec-e5f6a7b8',
        startedAt: '2025-01-15T11:15:00Z',
        completedAt: '2025-01-15T11:15:02Z',
        duration: 1800,
        status: 'warning',
        triggerData: { booking_reference: 'FEL-2025-0888', payment_method: 'open_banking', amount: 3200.00 },
        stepsExecuted: 5,
        warning: { step: 'Find Invoice', message: 'Invoice not found, created new invoice' },
        version: '1.3'
      },
      {
        id: 'log-006',
        executionId: 'exec-f6a7b8c9',
        startedAt: '2025-01-14T16:45:00Z',
        completedAt: '2025-01-14T16:45:03Z',
        duration: 2900,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0887', payment_method: 'card', amount: 780.00 },
        stepsExecuted: 7,
        version: '1.3'
      },
      {
        id: 'log-007',
        executionId: 'exec-a7b8c9d0',
        startedAt: '2025-01-14T14:20:00Z',
        completedAt: '2025-01-14T14:20:06Z',
        duration: 6100,
        status: 'error',
        triggerData: { booking_reference: 'FEL-2025-0886', payment_method: 'open_banking', amount: 1500.00 },
        stepsExecuted: 2,
        error: { step: 'New Transaction', message: 'Invalid payment reference format' },
        version: '1.3'
      },
      {
        id: 'log-008',
        executionId: 'exec-b8c9d0e1',
        startedAt: '2025-01-14T10:00:00Z',
        completedAt: '2025-01-14T10:00:02Z',
        duration: 2400,
        status: 'success',
        triggerData: { booking_reference: 'FEL-2025-0885', payment_method: 'card', amount: 560.00 },
        stepsExecuted: 7,
        version: '1.2'
      }
    ]
  }
];

export const getPlaybookById = (id) => {
  return PLAYBOOKS.find(p => p.id === id);
};

// Helper to get description from first trigger node or generate from name
export const getPlaybookDescription = (playbook) => {
  if (!playbook.nodes || playbook.nodes.length === 0) return '';

  const triggerNode = playbook.nodes.find(n =>
    n.type.includes('webhook') ||
    n.type.includes('trigger') ||
    n.type.includes('cron') ||
    n.type.includes('schedule')
  );

  if (triggerNode?.parameters?.path) {
    return `Triggered by webhook: ${triggerNode.parameters.path}`;
  }
  if (triggerNode?.parameters?.rule?.interval) {
    return `Scheduled workflow running on a cron schedule`;
  }
  if (triggerNode?.parameters?.event) {
    return `Triggered by ${triggerNode.parameters.event} event`;
  }

  return `Automated workflow with ${playbook.nodes.length} steps`;
};

// Helper to derive schedule from n8n nodes
export const getPlaybookSchedule = (playbook) => {
  if (!playbook.nodes) return 'on_event';

  const triggerNode = playbook.nodes.find(n =>
    n.type.includes('cron') ||
    n.type.includes('schedule') ||
    n.type.includes('webhook') ||
    n.type.includes('trigger')
  );

  if (!triggerNode) return 'on_event';

  // Check for cron expression
  if (triggerNode.parameters?.rule?.interval?.[0]?.expression) {
    return triggerNode.parameters.rule.interval[0].expression;
  }

  // Check for interval mode (every X minutes)
  if (triggerNode.parameters?.triggerTimes?.item?.[0]?.mode === 'everyMinute') {
    const mins = triggerNode.parameters.triggerTimes.item[0].minute || 1;
    return `*/${mins} * * * *`;
  }

  // Webhook or event trigger
  if (triggerNode.type.includes('webhook') || triggerNode.type.includes('trigger')) {
    return 'on_event';
  }

  return 'on_event';
};

// Convert n8n nodes to legacy stepsDetail format for StepsView
export const getStepsDetail = (playbook) => {
  if (!playbook.nodes) return [];

  return playbook.nodes.map((node, index) => {
    // Determine step type from n8n node type
    let stepType = 'action';
    if (node.type.includes('webhook') || node.type.includes('trigger') || node.type.includes('cron') || node.type.includes('schedule')) {
      stepType = 'webhook';
    } else if (node.type.includes('if') || node.type.includes('switch')) {
      stepType = 'condition';
    } else if (node.type.includes('wait')) {
      stepType = 'delay';
    } else if (node.type.includes('loop') || node.type.includes('splitInBatches')) {
      stepType = 'loop';
    } else if (node.type.includes('code')) {
      stepType = 'transform';
    }

    // Determine integration from node type
    let integration = null;
    if (node.type.includes('felloh')) {
      integration = 'felloh';
    } else if (node.type.includes('pipedrive')) {
      integration = 'pipedrive';
    } else if (node.type.includes('xero')) {
      integration = 'xero';
    } else if (node.type.includes('slack')) {
      integration = 'slack';
    } else if (node.type.includes('email') || node.type.includes('Email')) {
      integration = 'email';
    }

    return {
      id: node.id,
      name: node.name,
      type: stepType,
      description: node.parameters?.operation
        ? `${node.parameters.operation} operation`
        : `Step ${index + 1} of the workflow`,
      config: {
        ...node.parameters,
        integration
      }
    };
  });
};

export const getStepTypeIcon = (type, integration) => {
  // Integration-specific icons
  if (integration) {
    const integrationIcons = {
      pipedrive: '🔶',
      xero: '💠',
      email: '📧',
      slack: '💬',
      felloh: '💳'
    };
    return integrationIcons[integration] || '🔌';
  }

  const icons = {
    webhook: '🔗',
    condition: '🔀',
    action: '⚡',
    delay: '⏱️',
    schedule: '📅',
    loop: '🔄',
    transform: '🔄'
  };
  return icons[type] || '📦';
};

export const getStepTypeBadgeColor = (type, integration) => {
  // Integration-specific colors
  if (integration) {
    const integrationColors = {
      pipedrive: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
      xero: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
      email: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      slack: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-300',
      felloh: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300'
    };
    return integrationColors[integration] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }

  const colors = {
    webhook: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    condition: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    action: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    delay: 'bg-violet-100 text-violet-800 dark:bg-violet-800 dark:text-violet-300',
    schedule: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
    loop: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
    transform: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300'
  };
  return colors[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
};

export const getEventById = (eventId) => {
  return EVENTS.find(e => e.id === eventId);
};

export const getIntegrationById = (integrationId) => {
  return INTEGRATIONS.find(i => i.id === integrationId);
};

export const getEventsByCategory = (category) => {
  return EVENTS.filter(e => e.category === category);
};

export const getScheduleLabel = (schedule) => {
  if (!schedule || schedule === 'on_event') {
    return 'On event';
  }

  const parts = schedule.split(' ');
  if (parts.length !== 5) {
    return schedule;
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Every X minutes
  if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const mins = minute.replace('*/', '');
    return `Every ${mins} mins`;
  }

  // Every X hours
  if (minute === '0' && hour.startsWith('*/') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const hours = hour.replace('*/', '');
    return `Every ${hours} hours`;
  }

  // Daily at specific time
  if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const h = parseInt(hour, 10);
    const m = parseInt(minute, 10);
    const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    return `Daily at ${time}`;
  }

  // Weekly
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const day = days[parseInt(dayOfWeek, 10)] || dayOfWeek;
    return `Weekly on ${day}`;
  }

  // Monthly
  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    return `Monthly on day ${dayOfMonth}`;
  }

  // Hourly
  if (minute !== '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'Every hour';
  }

  return schedule;
};
